import { memo } from "react";
import {
  FormControlLabel,
  Grid2,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography
} from "@mui/material";
import { LockOpen, MedicalServices, TaskAlt } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import { TeamMember } from "../../../../features/Booking-Form/typings/team-member";
import { ScheduleDataStatusIndicator } from "../../../../features/Schedule/partial/ScheduleDataStatusIndicator.component";
import type { ScheduleExcelExportFetchConfig } from "../functions/schedule-excel-export-fetch.functions";
import { ScheduleExcelExport } from "./schedule-excel-export.component";

export type ScheduleCalendarTopToolbarProps = {
  currentView: "week" | "month";
  setCurrentView: (view: "week" | "month") => void;
  activateOfficeSafety: boolean;
  isFirstAidSelected: boolean;
  setIsFirstAidSelected: () => void;
  scheduleDataStatus: "error" | "loading" | "success" | "idle";
  todaysCheckin: BookingScheduleInterface[] | undefined;
  onMultiCheckinClick: () => void;
  unlockableSchedules: BookingScheduleInterface[] | undefined;
  onBulkUnlockClick: () => void;
  users?: TeamMember[];
  refetchAllSchedule: () => void;
  /** When false (resource calendar), week/month and nav stay; first aid, check-in, unlock, export, and status are hidden. */
  showColleagueToolbarActions?: boolean;
  /** Prefills export dialog period pickers when opened (visible calendar window). */
  excelExportInitialRange?: { startDate: string; endDate: string } | null;
  excelExportFetchConfig: ScheduleExcelExportFetchConfig;
};

export const ScheduleCalendarTopToolbar = memo(function ScheduleCalendarTopToolbar({
  currentView,
  setCurrentView,
  activateOfficeSafety,
  isFirstAidSelected,
  setIsFirstAidSelected,
  scheduleDataStatus,
  todaysCheckin,
  onMultiCheckinClick,
  unlockableSchedules,
  onBulkUnlockClick,
  users,
  refetchAllSchedule,
  showColleagueToolbarActions = true,
  excelExportInitialRange = null,
  excelExportFetchConfig
}: Readonly<ScheduleCalendarTopToolbarProps>) {
  const { t } = useTranslation();

  return (
    <Grid2
      container
      sx={{ alignItems: "center", pl: 1 }}
      data-testid="schedule-calendar-top-toolbar"
    >
      <Grid2
        container
        sx={{ width: "100%", alignItems: "center" }}
        data-testid="schedule-calendar-top-container"
      >
        <Grid2
          sx={{ alignContent: "center", ml: 0.5 }}
          data-testid="schedule-calendar-top-item-switch-monthview"
        >
          <RadioGroup
            aria-label="Views"
            style={{ flexDirection: "row" }}
            name="views"
            value={currentView}
            onChange={e => setCurrentView(e.target.value as "week" | "month")}
          >
            <FormControlLabel value="week" control={<Radio />} label={t("Week")} />
            <FormControlLabel value="month" control={<Radio />} label={t("Month")} />
          </RadioGroup>
        </Grid2>

        <Grid2
          size={{ sm: "grow" }}
          sx={{
            display: "flex",
            justifyContent: "space-between"
          }}
          data-testid="schedule-calendar-top-container-btn-group"
        >
          <Grid2 container data-testid="schedule-calendar-top-container-btn-items1">
            {showColleagueToolbarActions && activateOfficeSafety && (
              <Grid2 data-testid="schedule-calendar-item-firstAidBooking">
                <Tooltip title={<Typography>{t("showIsFirstAidUserBooking")}</Typography>}>
                  <span>
                    <IconButton
                      data-testid="firstAid-booking-schedule-btn"
                      onClick={setIsFirstAidSelected}
                      color={isFirstAidSelected ? "error" : "default"}
                      disabled={scheduleDataStatus === "loading" || scheduleDataStatus === "error"}
                      size="large"
                    >
                      <MedicalServices />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid2>
            )}

            {showColleagueToolbarActions && todaysCheckin && (
              <Grid2 data-testid="schedule-calendar-item-multiCheckin">
                <IconButton
                  data-testid="multiCheckin-btn"
                  onClick={onMultiCheckinClick}
                  size="large"
                >
                  <TaskAlt />
                </IconButton>
              </Grid2>
            )}

            {showColleagueToolbarActions && unlockableSchedules && (
              <Grid2 data-testid="schedule-calendar-item-unlockSchedules">
                <IconButton
                  data-testid="unlock-schedule-fast-btn"
                  onClick={onBulkUnlockClick}
                  size="large"
                >
                  <LockOpen />
                </IconButton>
              </Grid2>
            )}
          </Grid2>

          {showColleagueToolbarActions && (
            <Grid2 display={"flex"} data-testid="schedule-calendar-top-container-btn-items2">
              <Grid2>
                <ScheduleExcelExport
                  isDisabled={scheduleDataStatus !== "success"}
                  users={users}
                  fetchConfig={excelExportFetchConfig}
                  initialExportRange={excelExportInitialRange}
                />
              </Grid2>

              <Grid2>
                <ScheduleDataStatusIndicator
                  scheduleDataStatus={scheduleDataStatus}
                  refetchAllSchedule={refetchAllSchedule}
                />
              </Grid2>
            </Grid2>
          )}
        </Grid2>
      </Grid2>
    </Grid2>
  );
});
